import { z } from "zod";

import { solutionRecommendationsSchema } from "./solution-recommendations.js";

/**
 * Wire contract for the M2 admin HITL presentation-review surface
 * (issue #144) — the shapes exchanged between the admin-UI and the
 * orchestrator's `/api/presentations/:id`,
 * `/api/presentations/:id/review`, and
 * `/api/review/pending-presentations` endpoints.
 *
 * Sibling of `./review.ts` for the M1 curator-draft surface; kept
 * separate because the M2 review is single-decision (approve or
 * reject the whole presentation) rather than per-row batch decisions.
 *
 * The verdict is folded into `solution_presentations.status`
 * (`pending_review → approved | rejected | expired | failed`); the
 * `reviewedAt / reviewedBy / reviewNotes` columns carry the decision
 * metadata for the human-driven verdicts. `audit_log` keeps the full
 * decision trail under the `presentation_review_*` event-type family.
 */

/**
 * Memo snapshot served alongside the presentation — the operator
 * reviews memo + presentation together. The fields here mirror the
 * attacker-influenced surfaces called out in the XSS-safety pattern
 * (`apps/admin-ui/tests/unit/EntryDetail.test.tsx`): every string is
 * rendered as literal text by React's text-interpolation, never via
 * `dangerouslySetInnerHTML` or an HTML-permissive markdown renderer.
 *
 * Some optional fields are nullable to model legacy memos that
 * pre-date the field's introduction in `problem_memos.schema.ts`.
 */
export const presentationReviewMemoSchema = z.object({
  id: z.string().uuid(),
  /** First 8 hex chars of the memo UUID — used as an inbox-friendly handle. */
  shortId: z.string().min(1),
  /** Optional Drive file id for the rendered memo doc. Null when no upload occurred. */
  driveFileId: z.string().nullable(),
  /**
   * Mirror of the `problem_memos` columns the Consultant populates
   * from the public chatbot conversation. Every field below is
   * attacker-influenced — the chatbot visitor types into them — and
   * MUST be rendered with React's text-interpolation, never
   * `dangerouslySetInnerHTML` or an HTML-permissive markdown
   * renderer. See `docs/decisions/2026-06-15-admin-ui-xss-safe-rendering.md`.
   */
  mission: z.string().nullable(),
  vision: z.string().nullable(),
  objectives: z.array(z.string()),
  valueAdded: z.string().nullable(),
  bottlenecks: z.array(z.string()),
  /**
   * Structured budget shape (issue #573). Each numeric arm is a
   * `numeric(14,2)` value serialised as a string by Drizzle; null
   * when the client didn't volunteer that specific bound. `currency`
   * is an ISO 4217 code (`GBP` / `USD` / `EUR` / …), null when the
   * client declined to share any numbers. When the client declined,
   * all four fields are null.
   */
  budget: z.object({
    min: z.string().nullable(),
    target: z.string().nullable(),
    max: z.string().nullable(),
    currency: z.string().nullable(),
  }),
  assumptions: z.array(z.string()),
  techStack: z.array(z.string()),
  currentWorkflow: z.string().nullable(),
  decisionMaker: z.string().nullable(),
  urgency: z.string().nullable(),
});

export type PresentationReviewMemo = z.infer<typeof presentationReviewMemoSchema>;

/**
 * The GET `/api/presentations/:id` response — the surface the admin
 * review panel renders against. Combines the memo, the validator-
 * enriched recommendations, the presentation-level validation score,
 * the lifecycle state, and (when terminal) the human verdict.
 */
export const presentationReviewResponseSchema = z.object({
  id: z.string().uuid(),
  /** First 8 hex chars of the presentation UUID — for inbox-friendly display. */
  shortId: z.string().min(1),
  /**
   * `client_sessions.client_name` looked up via
   * `problem_memos.client_id`. Null when the memo has been
   * anonymised (2y sweeper — see `docs/decisions/2026-05-19-pii-handling.md`)
   * or when the underlying `client_sessions` row is absent. The
   * admin-ui uses these two fields to render a friendly page title
   * instead of a raw UUID (issue #575).
   */
  clientName: z.string().nullable(),
  /** `client_sessions.client_company`. Nullability parallels `clientName`. */
  clientCompany: z.string().nullable(),
  /**
   * `problem_memos.memo_number` for the presentation's parent memo — a
   * bigserial (issue #576) surfaced here so the admin-UI can build the
   * canonical `/presentations/00000042/review` URL (issue #603) and
   * render a friendly `Memo #NNNNNNNN` label (issue #575) when the
   * client identity has been anonymised. Always present — backfilled
   * deterministically in migration 0033; anonymisation sets
   * `client_id` to NULL but never touches `memo_number`.
   *
   * Not a per-presentation counter: two presentations that share a
   * memo (rare, but possible when the Analyst is re-dispatched after
   * a terminal verdict — the `solution_presentations_memo_id_draft_unique`
   * partial index only blocks concurrent drafts) share the same memo
   * number. The route resolver deterministically returns the most
   * recent presentation (`created_at DESC LIMIT 1`) for the memo
   * number, matching an operator's "which presentation am I working
   * on now" mental model.
   */
  memoNumber: z.number().int().positive(),
  memo: presentationReviewMemoSchema,
  status: z.enum([
    "draft",
    "pending_review",
    "approved",
    "rejected",
    "expired",
    "failed",
    "finalised",
    "sent",
  ]),
  matchValidationStatus: z.enum([
    "pending",
    "incomplete",
    "validated",
    "flagged",
  ]),
  /** 0.00–1.00 fraction of recs the validator kept. Null until validation runs. */
  matchValidationScore: z.number().min(0).max(1).nullable(),
  recommendations: solutionRecommendationsSchema,
  /** ISO-8601 wall-clock deadline for the human verdict. Null outside `pending_review`. */
  reviewDeadlineAt: z.string().datetime().nullable(),
  /** ISO-8601 timestamp of the human verdict. Null on system-driven paths or pre-decision. */
  reviewedAt: z.string().datetime().nullable(),
  /** Admin email from the Supabase JWT. Null when no human verdict has been recorded. */
  reviewedBy: z.string().nullable(),
  /** Optional rejection rationale (or arbitrary free text from the approver). */
  reviewNotes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type PresentationReviewResponse = z.infer<
  typeof presentationReviewResponseSchema
>;

/**
 * The POST `/api/presentations/:id/review` body. Single-decision
 * surface — the reviewer approves or rejects the whole presentation.
 * `notes` is optional but recommended on the reject path so the
 * audit trail records the rationale.
 */
export const presentationReviewSubmitBodySchema = z.object({
  decision: z.enum(["approved", "rejected"]),
  notes: z.string().max(5_000).optional(),
});

export type PresentationReviewSubmitBody = z.infer<
  typeof presentationReviewSubmitBodySchema
>;

/**
 * The POST `/api/presentations/:id/review` response — echoes back the
 * verdict + decision metadata for an immediate UI refresh without a
 * follow-up GET.
 */
export const presentationReviewSubmitResponseSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(["approved", "rejected"]),
  reviewedAt: z.string().datetime(),
  reviewedBy: z.string(),
  reviewNotes: z.string().nullable(),
});

export type PresentationReviewSubmitResponse = z.infer<
  typeof presentationReviewSubmitResponseSchema
>;

/**
 * One row in the queue rendered by `GET /api/review/pending-presentations`.
 * Carries enough fields to render a compact list-item without a per-row
 * fetch — the reviewer clicks through to the full surface for the
 * details. Mirrors the column choices in the curator-draft
 * `PendingReviewResponse` so the Reviews tab can render a homogeneous list.
 */
export const pendingPresentationItemSchema = z.object({
  id: z.string().uuid(),
  shortId: z.string().min(1),
  memoShortId: z.string().min(1),
  /**
   * `problem_memos.memo_number` for the parent memo (issue #603). The
   * `/review` queue link renders `/presentations/<padded>/review`
   * instead of the UUID form (URLs carry no `#` prefix — the `#` is
   * display-only). See the sibling `memoNumber` field on
   * {@link presentationReviewResponseSchema} for the shared caveat
   * about non-draft presentations per memo.
   */
  memoNumber: z.number().int().positive(),
  status: z.literal("pending_review"),
  matchValidationStatus: z.enum(["validated", "flagged"]),
  matchValidationScore: z.number().min(0).max(1).nullable(),
  /**
   * `solutionType` discriminator from the recommendations jsonb,
   * surfaced so the queue item can show a one-glance summary
   * (assembled vs custom vs hybrid) without parsing the full payload.
   */
  solutionType: z.string().nullable(),
  /** Count of `tools[]` entries (kept + removed) — surfaces in the queue. */
  recommendationCount: z.number().int().nonnegative(),
  /** Count of `removed` recommendations — surfaces as a queue badge. */
  removedCount: z.number().int().nonnegative(),
  reviewDeadlineAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});

export type PendingPresentationItem = z.infer<typeof pendingPresentationItemSchema>;

export const pendingPresentationsResponseSchema = z.object({
  presentations: z.array(pendingPresentationItemSchema),
});

export type PendingPresentationsResponse = z.infer<
  typeof pendingPresentationsResponseSchema
>;

/**
 * One row in the nav-level Presentations queue served by
 * `GET /api/presentations/recent` (issue #575). Broader than
 * `PendingPresentationItem` — includes decided rows so the operator
 * can browse recent verdicts alongside the pending queue — and
 * carries the friendly client identity + memo number so rows don't
 * expose UUIDs. Terminal `status` values are the four verdict
 * outcomes plus `pending_review`; `draft` / `finalised` / `sent`
 * never surface here because they don't participate in HITL review.
 */
export const recentPresentationItemSchema = z.object({
  id: z.string().uuid(),
  shortId: z.string().min(1),
  /** Same shape + rationale as {@link presentationReviewResponseSchema.memoNumber}. */
  memoNumber: z.number().int().positive(),
  status: z.enum([
    "pending_review",
    "approved",
    "rejected",
    "expired",
    "failed",
  ]),
  matchValidationStatus: z.enum([
    "pending",
    "incomplete",
    "validated",
    "flagged",
  ]),
  matchValidationScore: z.number().min(0).max(1).nullable(),
  solutionType: z.string().nullable(),
  /** Null once the memo has been anonymised (2y sweeper). */
  clientName: z.string().nullable(),
  clientCompany: z.string().nullable(),
  /** Non-null in `pending_review`; null once the row transitions terminal. */
  reviewDeadlineAt: z.string().datetime().nullable(),
  /** Non-null on `approved` / `rejected` verdicts; null on `expired` / `failed` / `pending_review`. */
  reviewedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});

export type RecentPresentationItem = z.infer<typeof recentPresentationItemSchema>;

export const recentPresentationsResponseSchema = z.object({
  presentations: z.array(recentPresentationItemSchema),
});

export type RecentPresentationsResponse = z.infer<
  typeof recentPresentationsResponseSchema
>;

/**
 * Human-decided verdicts surfaced on the past-reviews table (issue
 * #606). `failed` is deliberately excluded — that's a system-driven
 * terminal, not a decision an operator makes, and the past-reviews
 * table is scoped to Tomer's own decision history for consistency +
 * auditability.
 */
export const decidedPresentationDecisionSchema = z.enum([
  "approved",
  "rejected",
  "expired",
]);

export type DecidedPresentationDecision = z.infer<
  typeof decidedPresentationDecisionSchema
>;

/**
 * One row on the past-reviews table served by
 * `GET /api/presentations/decided` (issue #606). Narrower than
 * {@link recentPresentationItemSchema}:
 *
 * - `decision` is a 3-value union (no `pending_review` / `failed`) so
 *   the backend filter is enforced at the schema boundary and the
 *   frontend gets static exhaustiveness on the filter chips.
 * - `decidedAt` is always present — `COALESCE(reviewed_at, updated_at)`
 *   projected server-side so ordering + display share one column.
 * - `reviewedBy` + `reviewNotes` are surfaced (null for `expired`
 *   rows — the sweeper is not a reviewer).
 *
 * `clientName` / `clientCompany` fall back to null on 2y-anonymised
 * memos (see `docs/decisions/2026-05-19-pii-handling.md`); the
 * `memoNumber` bigserial is always present and drives the friendly
 * `Memo #NNNNNNNN` fallback + the canonical review URL.
 */
export const decidedPresentationItemSchema = z.object({
  id: z.string().uuid(),
  shortId: z.string().min(1),
  memoNumber: z.number().int().positive(),
  decision: decidedPresentationDecisionSchema,
  matchValidationStatus: z.enum([
    "pending",
    "incomplete",
    "validated",
    "flagged",
  ]),
  matchValidationScore: z.number().min(0).max(1).nullable(),
  solutionType: z.string().nullable(),
  clientName: z.string().nullable(),
  clientCompany: z.string().nullable(),
  /** ISO-8601 wall-clock of the human decision (or expiry sweep for `expired`). */
  decidedAt: z.string().datetime(),
  /** Admin email from the Supabase JWT. Null for `expired` (system-driven). */
  reviewedBy: z.string().nullable(),
  /** Free-text notes the reviewer attached. Null for `expired`. */
  reviewNotes: z.string().nullable(),
  createdAt: z.string().datetime(),
});

export type DecidedPresentationItem = z.infer<
  typeof decidedPresentationItemSchema
>;

export const decidedPresentationsResponseSchema = z.object({
  presentations: z.array(decidedPresentationItemSchema),
});

export type DecidedPresentationsResponse = z.infer<
  typeof decidedPresentationsResponseSchema
>;

/**
 * Query params for `GET /api/presentations/decided`. All optional; the
 * response defaults to "all three decisions, no date bounds, latest
 * 50 rows" when none are supplied.
 *
 * `decision` is a comma-separated list of the three verdict values,
 * validated by regex here so the schema itself is load-bearing —
 * unknown tokens fail at the schema boundary (400 `invalid_query`)
 * rather than in a re-parse step. `from` / `to` are ISO date-only
 * (`YYYY-MM-DD`) or full ISO datetimes — parsed leniently server-side
 * via `new Date()`. `limit` is capped at 200 by the schema.
 */
export const decidedPresentationsQuerySchema = z.object({
  decision: z
    .string()
    .regex(/^(approved|rejected|expired)(,(approved|rejected|expired))*$/, {
      message: "must be a comma-separated list of approved|rejected|expired",
    })
    .optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
});

export type DecidedPresentationsQuery = z.infer<
  typeof decidedPresentationsQuerySchema
>;
